import { captureClickIdFromUrl } from './auto-capture'
import { readCookie } from './cookie'
import { generateExternalEventId } from './external-event-id'
import { sha256Hex } from './hash'
import { hasPrivacySignal } from './privacy'
import { drainRetryQueue, sendTrack } from './transport'

import type { EdgelinkConfig, EventType, TrackPayload, TrackProps } from './types'

const DEFAULT_API_BASE_URL = 'https://api.edgelink.me'

/**
 * `@edgelinkapp/sdk-web`의 진입 클래스.
 *
 * 라이프사이클:
 *   1. `new Edgelink(config)` — 생성자에서 설정 검증만 수행
 *   2. `.init()` — 페이지 로드 시 호출. URL의 `?el_cid=`를 쿠키로 이동 + 큐 drain
 *   3. `.track(eventName, props)` / `.identify(externalUserId, traits)` — 이벤트 전송
 *
 * GPC/DNT 활성 시 모든 track 호출은 no-op.
 * `sale` 이벤트는 콘솔 경고 + no-op (옵션 `throwOnSale: true`로 예외 발생).
 */
class Edgelink {
  readonly #config: Required<EdgelinkConfig>
  #cachedClickId: string | null = null

  constructor(config: EdgelinkConfig) {
    if (config.publishableKey == null || config.publishableKey === '') {
      throw new Error('@edgelinkapp/sdk-web: publishableKey is required')
    }
    if (!config.publishableKey.startsWith('pk_')) {
      throw new Error('@edgelinkapp/sdk-web: publishableKey must start with "pk_"')
    }
    this.#config = {
      publishableKey: config.publishableKey,
      apiBaseUrl: config.apiBaseUrl ?? DEFAULT_API_BASE_URL,
      cookieDomain: config.cookieDomain ?? '',
      throwOnSale: config.throwOnSale ?? false,
      respectPrivacySignals: config.respectPrivacySignals ?? true,
    }
  }

  /** 페이지 로드 시 1회 호출. URL의 ?el_cid= 캡처 + retry 큐 drain */
  init(): void {
    this.#cachedClickId = captureClickIdFromUrl(this.#config.cookieDomain)
    void drainRetryQueue({ apiBaseUrl: this.#config.apiBaseUrl, publishableKey: this.#config.publishableKey })
  }

  /**
   * 전환 이벤트 전송. fire-and-forget.
   *
   * @param eventName 1~64자 문자열 (예: 'signup', 'add_to_cart')
   * @param props - eventType은 props에서 추론하지 않고 별도 메소드(`trackSale` 등)를 두지 않는 대신
   *   value 존재로 'lead'/'custom' 둘 중 자유로움. 'sale'은 명시적으로 호출자가 지정.
   */
  track(eventName: string, props: TrackProps & { eventType?: EventType } = {}): void {
    if (this.#config.respectPrivacySignals && hasPrivacySignal()) return

    const eventType: EventType = props.eventType ?? 'lead'
    if (eventType === 'sale') {
      const msg = '@edgelinkapp/sdk-web: sale 이벤트는 서버용 secret 키 (@edgelinkapp/sdk-node)로만 보낼 수 있습니다.'
      if (this.#config.throwOnSale) throw new Error(msg)
      if (typeof console !== 'undefined') console.warn(msg)
      return
    }

    void this.#sendEvent(eventName, eventType, props)
  }

  /**
   * 고객사 user.id 기반 매핑 등록.
   * 본 MVP에서는 lead 이벤트 형태로 매핑 row만 적재. 매칭 활용은 후행 spec에서.
   */
  identify(externalUserId: string, traits: { email?: string } = {}): void {
    if (externalUserId === '') return
    this.track('identify', { externalUserId, email: traits.email, eventType: 'lead' })
  }

  /** 현재 캐시된 clickId (캡처 직후 또는 쿠키에서 읽음) */
  getClickId(): string | null {
    return this.#cachedClickId ?? readCookie()
  }

  async #sendEvent(eventName: string, eventType: EventType, props: TrackProps): Promise<void> {
    const clickId = this.getClickId()
    const customerEmailHash =
      props.email != null && props.email !== '' ? await sha256Hex(props.email.toLowerCase()) : null
    const payload: TrackPayload = {
      clickId,
      externalUserId: props.externalUserId ?? null,
      externalEventId: props.externalEventId ?? generateExternalEventId(eventName),
      eventType,
      eventName,
      value: props.value ?? 0,
      currency: props.currency ?? 'KRW',
      customerEmailHash,
      metadata: props.metadata ?? null,
    }
    sendTrack(payload, { apiBaseUrl: this.#config.apiBaseUrl, publishableKey: this.#config.publishableKey })
  }
}

export { Edgelink }
